import { ResourceType } from '@models';
import { API_URL } from 'src/app/core/constants/api';

import { getAbsoluteApiUrl } from './get-absolute-api-url';
import { getPathByType } from './resource-path';

export const buildResourceURL = (
  resourceType: ResourceType | string,
  rid: number,
  suffix: string
) => {
  let path: string;

  if (
    typeof resourceType === 'number' &&
    [
      ResourceType.ROOM,
      ResourceType.FILE,
      ResourceType.DISCUSSION,
      ResourceType.EVENT,
    ].includes(resourceType)
  ) {
    path = getPathByType(resourceType);
  } else {
    path = String(resourceType);
  }

  return `${getAbsoluteApiUrl()}/${path}/${rid}/${suffix}`;
};

export const getDownloadUrl = (
  resourceType: ResourceType | string,
  rid: number
) => buildResourceURL(resourceType, rid, 'download');

export const getDownloadSignedUrl = (
  resourceType: ResourceType | string,
  rid: number
) => buildResourceURL(resourceType, rid, 'downloadsigned');

export const getDownloadUrlWithAnnotations = (
  resourceType: ResourceType | string,
  rid: number
) => buildResourceURL(resourceType, rid, 'download?annotations=true');

export const buildFileURL = (
  rids: number | { rid: number } | { rid: number }[],
  filename: string,
  suffix: string
) => {
  let query = '';

  if (Array.isArray(rids)) {
    rids.forEach((item, index) => {
      if (index > 0) {
        query += '&';
      }

      query += `rid=${item.rid}`;
    });
  } else if (typeof rids === 'object') {
    query = `rid=${rids.rid}`;
  } else if (typeof rids === 'number') {
    query = `rid=${rids}`;
  }

  return `${API_URL}/files/${filename}?${query}${suffix}`;
};

export const getDownloadZipUrl = (
  rids: number | { rid: number } | { rid: number }[],
  areZippedFilenamesShortened: boolean
) =>
  buildFileURL(rids, 'STR.zip', `&shortnames=${areZippedFilenamesShortened}`);

export const getDownloadMergeUrl = (
  rids: number | { rid: number } | { rid: number }[]
) => buildFileURL(rids, 'STR.pdf', '&annotations=true');

export const getEventIcsExportUrl = (rid: number) =>
  `${API_URL}/events/${rid}/export`;

export const getEventExcelExportUrl = (rid: number) =>
  `${getEventIcsExportUrl(rid)}?format=xlsx`;

export const getSystemConditionsDownloadUrl = (): string =>
  `${API_URL}/system/conditions/download`;

export const getUrlConfig = (
  pid: Nullable<number>,
  type: ResourceType,
  parentType: Nullable<ResourceType>
) => {
  if (parentType !== null) {
    if (
      parentType === ResourceType.USER ||
      (parentType === ResourceType.COMMUNITY && type === ResourceType.USER)
    ) {
      return `users/${pid}/config`;
    } else if (
      parentType === ResourceType.GROUP ||
      (parentType === ResourceType.COMMUNITY && type === ResourceType.GROUP)
    ) {
      return `groups/${pid}/config`;
    } else if (parentType === ResourceType.ROOM) {
      return `rooms/${pid}/config?type=${type}`;
    } else if (parentType === ResourceType.EVENT) {
      return `events/${pid}/config`;
    } else if (parentType === ResourceType.AGENDA_POINT) {
      return `agendapoints/${pid}/config`;
    }
  } else {
    if (type === ResourceType.USER) {
      return 'users/config';
    } else if (type === ResourceType.GROUP) {
      return 'groups/config';
    } else if (type === ResourceType.ROOM) {
      return 'rooms/config';
    } else if (type === ResourceType.COMMUNITY) {
      return 'community/config';
    }
  }

  return '';
};

export const getUrlPost = (
  parentRid: Nullable<number>,
  parentType: Nullable<ResourceType>,
  resourceType: ResourceType,
  processType?: 'signSeal'
) => {
  let url = '';

  if (parentType === ResourceType.ROOM) {
    url += 'rooms/' + parentRid;
  } else if (parentType === ResourceType.EVENT) {
    url += 'events/' + parentRid;
  } else if (parentType === ResourceType.AGENDA_POINT) {
    url += 'agendapoints/' + parentRid;
  } else if (parentType === ResourceType.USER) {
    url += 'users/' + parentRid;
  } else if (parentType === ResourceType.GROUP) {
    url += 'groups/' + parentRid;
  }

  if (resourceType === ResourceType.ROOM) {
    url += '/rooms';
  } else if (resourceType === ResourceType.FILE) {
    url += processType === 'signSeal' ? '/files/sealsign' : '/files';
  } else if (resourceType === ResourceType.DISCUSSION) {
    url += '/discussions';
  } else if (resourceType === ResourceType.EVENT) {
    url += '/events';
  } else if (resourceType === ResourceType.AGENDA_POINT) {
    url += '/agendapoints';
  } else if (resourceType === ResourceType.ACTIVITY) {
    url += '/activities';
  }

  return url;
};
